<script>
import SpellCheckedText from "@/components/SpellCheckedText.vue";
import PhotoBox from "@/components/PhotoBox.vue";

export default {
    name: "LPTemp7",
    components: {
        SpellCheckedText,
        PhotoBox
    },
    props: ["proposal"],
    data() {
        return {
            footerImage: ""
        };
    },
    computed: {
        project() {
            return this.proposal.projects && this.proposal.projects.length > 0
                ? this.proposal.projects[0]
                : null;
        }
    },
    mounted() {
        this.generateGradients();
    },
    methods: {
        generateGradients() {
            const fCanvas = document.createElement('canvas');
            fCanvas.width = 1130;
            fCanvas.height = 400;
            const fCtx = fCanvas.getContext('2d');
            const fGrad = fCtx.createLinearGradient(0, 300, 0, 0);
            fGrad.addColorStop(0, 'rgba(0, 0, 0, 0.4)');
            fGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
            fCtx.fillStyle = fGrad;
            fCtx.fillRect(0, 0, 1130, 400);
            this.footerImage = fCanvas.toDataURL();
        }
    }
};
</script>

<template>
  <div class="main-container" :style="{
    '--header-color': proposal.style.colorScheme.header,
    '--body-color': proposal.style.colorScheme.body,
    '--footer-color': proposal.style.colorScheme.footer,
    '--heading-color': proposal.style.heading.color || '#ffffff',
    '--heading-size': proposal.style.heading.size + 'px',
    '--heading-font': proposal.style.heading.font,
    '--heading-weight': proposal.style.heading.bold ? 'bold' : 'normal',
    '--heading-style': proposal.style.heading.italic ? 'italic' : 'normal',
    '--heading-decoration': proposal.style.heading.underline ? 'underline' : 'none',
    '--label-color': proposal.style.label.color || '#ffffff',
    '--label-size': proposal.style.label.size + 'px',
    '--label-font': proposal.style.label.font,
    '--label-weight': proposal.style.label.bold ? 'bold' : 'normal',
    '--label-style': proposal.style.label.italic ? 'italic' : 'normal',
    '--label-decoration': proposal.style.label.underline ? 'underline' : 'none',
    '--text-color': proposal.style.text.color || '#ffffff',
    '--text-size': proposal.style.text.size + 'px',
    '--text-font': proposal.style.text.font,
    '--text-weight': proposal.style.text.bold ? 'bold' : 'normal',
    '--text-style': proposal.style.text.italic ? 'italic' : 'normal',
    '--text-decoration': proposal.style.text.underline ? 'underline' : 'none',
  }">
    <div class="lastPage landscape-split">
      <div class="left-panel">
        <div class="company-details">
          <div class="logo-wrapper" v-if="proposal.logo">
            <div class="logo-box photobox">
              <PhotoBox :project="proposal" type="logo" bgColor="transparent" objectFit="contain" />
            </div>
          </div>

          <p class="comp-name">
            <SpellCheckedText :value="proposal.name || ''" @input="proposal.name = $event" />
          </p>
          <p>
            <SpellCheckedText :value="proposal.address || ''" @input="proposal.address = $event" />
          </p>
          <p>
            <SpellCheckedText :value="proposal.city || ''" @input="proposal.city = $event" />{{ proposal.city ? ', ' :
              '' }}
            <SpellCheckedText :value="proposal.stateProvince || ''" @input="proposal.stateProvince = $event" />
          </p>
          <p v-if="proposal.contactNo">
            <SpellCheckedText :value="proposal.contactNo || ''" @input="proposal.contactNo = $event" />
          </p>
          <p v-if="proposal.email">
            <SpellCheckedText :value="proposal.email || ''" @input="proposal.email = $event" />
          </p>
          <p v-if="proposal.website">
            <SpellCheckedText :value="proposal.website || ''" @input="proposal.website = $event" />
          </p>
        </div>
      </div>

      <div class="right-panel photobox">
        <PhotoBox v-if="project" :project="project" height="211mm" :bgColor="proposal.style.colorScheme && proposal.style.colorScheme.footer"
          objectFit="cover" />
        <div v-else class="placeholder-image"></div>

        <div class="footer-overlay" v-if="project && footerImage">
          <img :src="footerImage" class="w-full h-full" />
        </div>

        <div class="project-info" v-if="project">
          <p class="project-name">
            <SpellCheckedText :value="project.projectName || ''" @input="project.projectName = $event" />
          </p>
          <p class="project-location">
            <SpellCheckedText :value="project.city || ''" @input="project.city = $event" />{{ project.city ? ', ' : ''
            }}
            <SpellCheckedText :value="project.stateProvince || ''" @input="project.stateProvince = $event" />
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.main-container {
  display: block;
  width: 296.83mm;
  height: 211mm;
  min-height: 211mm;
  position: relative;
  box-sizing: border-box;
  overflow: hidden;

  &:hover {
    .photobox :deep(.photo-overlay) {
      opacity: 1;
    }

    .photobox :deep(img) {
      opacity: 0.9;
    }
  }

  .lastPage {
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 100%;

    .left-panel {
      width: 35%;
      height: 100%;
      background-color: var(--header-color);
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding: 40px;
      box-sizing: border-box;

      .company-details {
        text-align: left;

        .logo-wrapper {
          display: flex;
          justify-content: center;
          margin-bottom: 5px;

          .logo-box {
            margin-bottom: 5px;
            width: 250px;
            height: 80px;
          }
        }

        p {
          margin: 0;
          margin-bottom: 2px;
          color: var(--text-color);
          font-size: var(--text-size);
          font-family: var(--text-font);
          font-weight: var(--text-weight);
          font-style: var(--text-style);
          text-decoration: var(--text-decoration);
          line-height: 1.2;

          &.comp-name {
            color: var(--label-color);
            font-size: var(--label-size);
            font-family: var(--label-font);
            font-weight: var(--label-weight);
            font-style: var(--label-style);
            text-decoration: var(--label-decoration);
            margin-bottom: 8px;
          }
        }
      }
    }

    .right-panel {
      width: 65%;
      height: 100%;
      position: relative;

      .placeholder-image {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 1;
        background: var(--footer-color);
      }

      .footer-overlay {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        z-index: 1;
      }

      .project-info {
        position: absolute;
        left: 30px;
        bottom: 30px;
        color: white;
        z-index: 50;
        pointer-events: auto;

        .project-name {
          font-size: 10px;
          font-family: Helvetica;
          font-weight: bold;
          margin: 0;
        }

        .project-location {
          font-size: 10px;
          font-family: Helvetica;
          font-style: italic;
          margin: 0;
        }
      }
    }
  }
}
</style>
